// "Automation map" page (FR first, EN): presentation-only data layered on about-automations.ts, which stays the
// source of truth for what each automation does. SOURCES follows the order of AUTOMATIONS[locale].items; the icon and
// mode are repeated so a test catches any reordering.

import type { AreaIconName } from '../components/AreaIcon';
import type { AutomationMode } from './about-automations';

export type SourceId = 'inbox' | 'app' | 'bank' | 'clock' | 'board' | 'meeting';

/** Where the work comes from, per automation, in the order of AUTOMATIONS[locale].items. */
export const SOURCES: { icon: AreaIconName; mode: AutomationMode; from: SourceId[] }[] = [
  { icon: 'requests', mode: 'choice', from: ['inbox', 'app'] },
  { icon: 'requests', mode: 'always', from: ['inbox'] },
  { icon: 'dues', mode: 'choice', from: ['clock'] },
  { icon: 'dues', mode: 'choice', from: ['bank'] },
  { icon: 'dues', mode: 'confirm', from: ['board'] },
  { icon: 'maintenance', mode: 'choice', from: ['inbox', 'app'] },
  { icon: 'maintenance', mode: 'confirm', from: ['board'] },
  { icon: 'maintenance', mode: 'choice', from: ['inbox'] },
  { icon: 'compliance', mode: 'choice', from: ['app'] },
  { icon: 'compliance', mode: 'always', from: ['clock'] },
  { icon: 'governance', mode: 'confirm', from: ['board'] },
  { icon: 'governance', mode: 'confirm', from: ['meeting'] },
  { icon: 'reservations', mode: 'choice', from: ['app'] },
  { icon: 'documents', mode: 'always', from: ['clock'] },
];

export const SOURCE_ORDER: SourceId[] = ['inbox', 'app', 'bank', 'clock', 'board', 'meeting'];
export const MODE_ORDER: AutomationMode[] = ['always', 'choice', 'confirm'];

/** One step of a scenario, shown as a small mock screen. */
export type StoryScreen =
  | { kind: 'email'; from: string; to: string; subject: string; time: string; attachment?: string }
  | { kind: 'clock'; time: string; text: string }
  | { kind: 'chips'; chips: string[]; text: string }
  | { kind: 'quote'; source: string; quote: string }
  | { kind: 'draft'; text: string; cite: string }
  | { kind: 'figures'; rows: [string, string][]; total: [string, string] }
  | { kind: 'checks'; items: string[] }
  | { kind: 'approve'; title: string; text: string; approve: string; edit: string; auto: string }
  | { kind: 'log'; lines: string[]; hash: string };

export interface StoryStep {
  lane: 'signal' | 'syndiqia' | 'you' | 'log';
  title: string;
  screen: StoryScreen;
}

export interface Story {
  icon: AreaIconName;
  tab: string;
  title: string;
  steps: StoryStep[];
}

export interface AutomationMapContent {
  eyebrow: string;
  title: [string, string];
  lead: string;
  stats: { automations: string; areas: string; modes: string; logged: string };
  back: string;
  explore: string;
  mapTitle: string;
  mapLead: string;
  columns: { sources: string; syndiqia: string; you: string };
  all: string;
  sources: Record<SourceId, { title: string; sub: string }>;
  gates: Record<AutomationMode, { title: string; text: string }>;
  open: string;
  close: string;
  storiesTitle: string;
  storiesLead: string;
  lanes: Record<StoryStep['lane'], string>;
  play: string;
  pause: string;
  example: string;
  stories: Story[];
  chainTitle: string;
  chainLead: string;
  chain: string[];
  closingTitle: string;
  closingText: string;
  closingCta: string;
  posterNote: string;
}

const fr: AutomationMapContent = {
  eyebrow: 'Les automatisations de Syndiqia',
  title: ['Le travail se fait.', 'Le conseil décide.'],
  lead: 'Chaque courriel, relevé bancaire, facture ou échéance déclenche un processus. Syndiqia lit, vérifie, prépare et suit; ce qui engage le syndicat attend votre feu vert.',
  stats: { automations: 'automatisations', areas: 'domaines de gestion', modes: 'niveaux d’autonomie', logged: 'des actions consignées' },
  back: 'Découvrir Syndiqia',
  explore: 'Explorer la carte',
  mapTitle: 'La carte des automatisations',
  mapLead: 'D’où vient le travail, ce que Syndiqia en fait, et où vous intervenez. Survolez ou touchez une automatisation pour suivre son chemin; ouvrez-la pour le détail.',
  columns: { sources: 'Ce qui arrive', syndiqia: 'Ce que Syndiqia fait', you: 'Qui décide' },
  all: 'Toutes',
  sources: {
    inbox: { title: 'Courriels entrants', sub: 'demandes@ · factures@' },
    app: { title: 'Application', sub: 'demandes, réservations, attestations' },
    bank: { title: 'Relevé bancaire', sub: 'fichier CSV importé' },
    clock: { title: 'Chaque jour', sub: 'soldes, échéances, résumé' },
    board: { title: 'Décisions du conseil', sub: 'budget, assemblée, soumissions' },
    meeting: { title: 'En séance', sub: 'présences, votes, enregistrement' },
  },
  gates: {
    always: { title: 'Agit seul', text: 'Rien à décider : le travail se fait, vous êtes informés.' },
    choice: { title: 'Vous confirmez, ou automatique', text: 'Commence en confirmation; passe en automatique quand ses propositions sont justes.' },
    confirm: { title: 'Vous décidez', text: 'Avis légaux, choix d’un entrepreneur, procès-verbaux : toujours une personne.' },
  },
  open: 'Voir le détail',
  close: 'Fermer',
  storiesTitle: 'Suivez un dossier',
  storiesLead: 'Trois situations de tous les jours, de l’arrivée jusqu’au journal.',
  lanes: { signal: 'Arrive', syndiqia: 'Syndiqia', you: 'Vous', log: 'Journal' },
  play: 'Lecture',
  pause: 'Pause',
  example: 'Exemple fictif',
  stories: [
    {
      icon: 'requests',
      tab: 'Une question à 22 h',
      title: 'Une copropriétaire écrit un soir de semaine',
      steps: [
        { lane: 'signal', title: 'Le courriel arrive', screen: { kind: 'email', from: 'Marie Tremblay · unité 304', to: 'demandes@erables.syndiqia.com', subject: 'Puis-je installer un climatiseur sur mon balcon?', time: '22 h 14' } },
        { lane: 'syndiqia', title: 'Il est classé', screen: { kind: 'chips', chips: ['Question', 'Non urgent', 'Unité 304', 'Demande no 87'], text: 'Classé en quelques secondes, au bon endroit.' } },
        { lane: 'syndiqia', title: 'La réponse est trouvée', screen: { kind: 'quote', source: 'Règlement de l’immeuble · art. 7.4 · p. 23', quote: '« Aucun appareil ne peut être fixé aux façades ou aux balcons sans l’autorisation écrite du conseil. »' } },
        { lane: 'syndiqia', title: 'Un brouillon est rédigé', screen: { kind: 'draft', text: 'Bonjour Marie, un climatiseur sur le balcon demande l’autorisation écrite du conseil. Transmettez-nous le modèle et l’emplacement prévu; nous vous répondrons après la prochaine réunion.', cite: 'Règlement · art. 7.4 · p. 23' } },
        { lane: 'you', title: 'Vous approuvez', screen: { kind: 'approve', title: 'Réponse prête', text: 'Demande d’autorisation : transmise au conseil pour décision.', approve: 'Approuver et envoyer', edit: 'Modifier', auto: 'En mode automatique, les réponses simples partent seules.' } },
        { lane: 'log', title: 'Envoyé et consigné', screen: { kind: 'log', lines: ['Réponse envoyée à 22 h 16', 'Preuve de remise reçue', 'Sources citées : art. 7.4, p. 23'], hash: '9f2c·41ab·e07d' } },
      ],
    },
    {
      icon: 'maintenance',
      tab: 'La facture du plombier',
      title: 'Un entrepreneur envoie sa facture',
      steps: [
        { lane: 'signal', title: 'La facture arrive', screen: { kind: 'email', from: 'Plomberie Laval inc.', to: 'factures@erables.syndiqia.com', subject: 'Facture 2291 · BT-0042', time: '9 h 02', attachment: 'facture-2291.pdf' } },
        { lane: 'syndiqia', title: 'Elle est lue', screen: { kind: 'figures', rows: [['Travaux', '1 150,00 $'], ['TPS 5 %', '57,50 $'], ['TVQ 9,975 %', '114,71 $']], total: ['Total', '1 322,21 $'] } },
        { lane: 'syndiqia', title: 'Elle est vérifiée', screen: { kind: 'checks', items: ['Bon fournisseur, bon de travail BT-0042', 'Travaux confirmés terminés', 'Dans l’estimation de 1 400 $', 'Numéro de facture jamais vu', 'TPS et TVQ exactes'] } },
        { lane: 'you', title: 'Vous approuvez', screen: { kind: 'approve', title: 'Facture conforme', text: 'Plomberie Laval · 1 322,21 $ · BT-0042', approve: 'Approuver', edit: 'Refuser', auto: 'Sous la limite que vous fixez, une facture conforme peut s’approuver seule.' } },
        { lane: 'log', title: 'Comptabilisée', screen: { kind: 'log', lines: ['Charge d’entretien : 1 322,21 $', 'Comptes fournisseurs (2010)', 'Carnet d’entretien mis à jour', 'Le paiement reste à la banque'], hash: '3b81·c9e2·77f0' } },
      ],
    },
    {
      icon: 'dues',
      tab: 'Un paiement en retard',
      title: 'Une cotisation n’est pas entrée',
      steps: [
        { lane: 'signal', title: 'Le tour du matin', screen: { kind: 'clock', time: '6 h 00', text: 'Syndiqia examine le compte de chaque unité.' } },
        { lane: 'syndiqia', title: 'Un retard est détecté', screen: { kind: 'chips', chips: ['Unité 112', '15 jours de retard', '412,00 $', 'Dépôts conciliés ✓'], text: 'Aucune relance tant que des dépôts restent à concilier.' } },
        { lane: 'syndiqia', title: 'La relance est rédigée', screen: { kind: 'draft', text: 'Hello Mr. Patel, our records show that the October contribution of $412.00 for unit 112 has not been received yet…', cite: 'Dans la langue du copropriétaire' } },
        { lane: 'you', title: 'Vous approuvez le lot', screen: { kind: 'approve', title: 'Lot de 3 relances', text: 'Étape 15 jours · unités 112, 205, 410', approve: 'Approuver le lot', edit: 'Voir chaque relance', auto: 'En mode automatique, les relances partent selon le calendrier prévu.' } },
        { lane: 'log', title: 'Envoyé, suivi prévu', screen: { kind: 'log', lines: ['3 relances envoyées, preuves de remise', 'Prochaine étape : 30 jours', 'Au-delà : le conseil décide'], hash: 'd4e6·0a19·5c3b' } },
      ],
    },
  ],
  chainTitle: 'Un journal que personne ne peut réécrire',
  chainLead: 'Chaque événement et chaque action automatique est enchaîné au précédent par une empreinte. Une modification après coup briserait la chaîne : le conseil peut le vérifier.',
  chain: ['Demande no 87 classée', 'Réponse approuvée', 'Facture 2291 vérifiée', 'Écriture comptabilisée', 'Relances envoyées', 'Résumé du matin'],
  closingTitle: 'Moins d’administration, de meilleures décisions.',
  closingText: 'Chaque automatisation, ses étapes et ses garde-fous sont décrits sur la page Découvrir.',
  closingCta: 'Retour à Découvrir',
  posterNote: 'Chaque action est consignée dans un journal que le conseil peut vérifier.',
};

const en: AutomationMapContent = {
  eyebrow: 'Syndiqia’s automations',
  title: ['The work gets done.', 'The board decides.'],
  lead: 'Every email, bank statement, invoice or deadline starts a process. Syndiqia reads, checks, prepares and follows up; anything that commits the syndicate waits for your go-ahead.',
  stats: { automations: 'automations', areas: 'management areas', modes: 'levels of autonomy', logged: 'of actions logged' },
  back: 'Discover Syndiqia',
  explore: 'Explore the map',
  mapTitle: 'The automation map',
  mapLead: 'Where the work comes from, what Syndiqia does with it, and where you step in. Hover or tap an automation to follow its path; open it for the details.',
  columns: { sources: 'What comes in', syndiqia: 'What Syndiqia does', you: 'Who decides' },
  all: 'All',
  sources: {
    inbox: { title: 'Incoming email', sub: 'demandes@ · factures@' },
    app: { title: 'The app', sub: 'requests, bookings, certificates' },
    bank: { title: 'Bank statement', sub: 'imported CSV file' },
    clock: { title: 'Every day', sub: 'balances, deadlines, digest' },
    board: { title: 'Board decisions', sub: 'budget, meeting, quotes' },
    meeting: { title: 'In the meeting', sub: 'attendance, votes, recording' },
  },
  gates: {
    always: { title: 'Acts on its own', text: 'Nothing to decide: the work gets done, you are kept informed.' },
    choice: { title: 'You confirm, or automatic', text: 'Starts in confirmation; switches to automatic once its proposals are right.' },
    confirm: { title: 'You decide', text: 'Legal notices, choosing a contractor, minutes: always a person.' },
  },
  open: 'See the details',
  close: 'Close',
  storiesTitle: 'Follow a case',
  storiesLead: 'Three everyday situations, from arrival to the log.',
  lanes: { signal: 'Arrives', syndiqia: 'Syndiqia', you: 'You', log: 'Log' },
  play: 'Play',
  pause: 'Pause',
  example: 'Fictional example',
  stories: [
    {
      icon: 'requests',
      tab: 'A question at 10 pm',
      title: 'An owner writes on a weeknight',
      steps: [
        { lane: 'signal', title: 'The email arrives', screen: { kind: 'email', from: 'Marie Tremblay · unit 304', to: 'demandes@erables.syndiqia.com', subject: 'Can I install an air conditioner on my balcony?', time: '10:14 pm' } },
        { lane: 'syndiqia', title: 'It is sorted', screen: { kind: 'chips', chips: ['Question', 'Not urgent', 'Unit 304', 'Request no. 87'], text: 'Sorted in seconds, in the right place.' } },
        { lane: 'syndiqia', title: 'The answer is found', screen: { kind: 'quote', source: 'Building by-laws · art. 7.4 · p. 23', quote: '“No appliance may be attached to the façades or balconies without the written authorization of the board.”' } },
        { lane: 'syndiqia', title: 'A draft is written', screen: { kind: 'draft', text: 'Hello Marie, an air conditioner on the balcony needs the board’s written authorization. Send us the model and the planned location; we will reply after the next meeting.', cite: 'By-laws · art. 7.4 · p. 23' } },
        { lane: 'you', title: 'You approve', screen: { kind: 'approve', title: 'Reply ready', text: 'Authorization request: passed to the board for a decision.', approve: 'Approve and send', edit: 'Edit', auto: 'In automatic mode, simple answers go out on their own.' } },
        { lane: 'log', title: 'Sent and logged', screen: { kind: 'log', lines: ['Reply sent at 10:16 pm', 'Proof of delivery received', 'Sources cited: art. 7.4, p. 23'], hash: '9f2c·41ab·e07d' } },
      ],
    },
    {
      icon: 'maintenance',
      tab: 'The plumber’s invoice',
      title: 'A contractor sends an invoice',
      steps: [
        { lane: 'signal', title: 'The invoice arrives', screen: { kind: 'email', from: 'Plomberie Laval inc.', to: 'factures@erables.syndiqia.com', subject: 'Invoice 2291 · WO-0042', time: '9:02 am', attachment: 'invoice-2291.pdf' } },
        { lane: 'syndiqia', title: 'It is read', screen: { kind: 'figures', rows: [['Work', '$1,150.00'], ['GST 5%', '$57.50'], ['QST 9.975%', '$114.71']], total: ['Total', '$1,322.21'] } },
        { lane: 'syndiqia', title: 'It is checked', screen: { kind: 'checks', items: ['Right contractor, work order WO-0042', 'Work confirmed completed', 'Within the $1,400 estimate', 'Invoice number never seen before', 'GST and QST correct'] } },
        { lane: 'you', title: 'You approve', screen: { kind: 'approve', title: 'Compliant invoice', text: 'Plomberie Laval · $1,322.21 · WO-0042', approve: 'Approve', edit: 'Refuse', auto: 'Under the limit you set, a compliant invoice can be approved on its own.' } },
        { lane: 'log', title: 'Posted', screen: { kind: 'log', lines: ['Maintenance expense: $1,322.21', 'Accounts payable (2010)', 'Maintenance logbook updated', 'Payment stays at the bank'], hash: '3b81·c9e2·77f0' } },
      ],
    },
    {
      icon: 'dues',
      tab: 'A late payment',
      title: 'A contribution has not come in',
      steps: [
        { lane: 'signal', title: 'The morning round', screen: { kind: 'clock', time: '6:00 am', text: 'Syndiqia reviews every unit’s account.' } },
        { lane: 'syndiqia', title: 'A late payment is found', screen: { kind: 'chips', chips: ['Unit 112', '15 days late', '$412.00', 'Deposits reconciled ✓'], text: 'No reminder while deposits remain unreconciled.' } },
        { lane: 'syndiqia', title: 'The reminder is written', screen: { kind: 'draft', text: 'Hello Mr. Patel, our records show that the October contribution of $412.00 for unit 112 has not been received yet…', cite: 'In the owner’s language' } },
        { lane: 'you', title: 'You approve the batch', screen: { kind: 'approve', title: 'Batch of 3 reminders', text: '15-day step · units 112, 205, 410', approve: 'Approve the batch', edit: 'See each reminder', auto: 'In automatic mode, reminders go out on the planned schedule.' } },
        { lane: 'log', title: 'Sent, follow-up planned', screen: { kind: 'log', lines: ['3 reminders sent, proofs of delivery', 'Next step: 30 days', 'Beyond that: the board decides'], hash: 'd4e6·0a19·5c3b' } },
      ],
    },
  ],
  chainTitle: 'A log no one can rewrite',
  chainLead: 'Every event and every automatic action is chained to the previous one by a fingerprint. Any later change would break the chain: the board can verify it.',
  chain: ['Request no. 87 sorted', 'Reply approved', 'Invoice 2291 checked', 'Entry posted', 'Reminders sent', 'Morning digest'],
  closingTitle: 'Less administration, better decisions.',
  closingText: 'Every automation, its steps and its guardrails are described on the Discover page.',
  closingCta: 'Back to Discover',
  posterNote: 'Every action is recorded in a log the board can verify.',
};

export const AUTOMATION_MAP: Record<'fr' | 'en', AutomationMapContent> = { fr, en };
